import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  productService,
  ProductPaginationParams,
  ProductFilterParams,
  BulkPriceUpdatePayload,
} from '../services/productService';
import { ProductFormData } from '../types/Product';

export const useProducts = (params?: ProductPaginationParams) => {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => productService.getAll(params),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useProductSearch = (name: string) => {
  return useQuery({
    queryKey: ['products', 'search', name],
    queryFn: () => productService.search(name),
    enabled: name.length > 0,
  });
};

export const useFilterProducts = (params?: ProductFilterParams | null) => {
  return useQuery({
    queryKey: ['products', 'filter', params],
    queryFn: () => productService.filter(params ?? undefined),
    enabled: params != null,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useProduct = (id: number) => {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => productService.getById(id),
    enabled: !!id,
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductFormData) => productService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProductFormData }) =>
      productService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

export const useBulkUpdateProductPrices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkPriceUpdatePayload) => productService.bulkUpdatePrices(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => productService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

